import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Download, Compass, Info, Sparkles, Lock } from "lucide-react";
import Shell from "@/components/Shell";
import { http } from "@/lib/api";
import { useApp } from "@/lib/store";
import { track } from "@/lib/analytics";

function printReview(data) {
  const rows = data.sections
    .map((s, i) => `<div style="margin:14px 0"><div style="font:600 11px/1.4 monospace;color:#C9A227">${String(i + 1).padStart(2, "0")} · ${s.title}</div><div style="font:400 14px/1.6 Georgia,serif;color:#333;margin-top:4px">${s.body}</div></div>`)
    .join("");
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<!doctype html><html><head><title>${data.title}</title><meta name="viewport" content="width=device-width,initial-scale=1"></head>
  <body style="max-width:640px;margin:32px auto;padding:0 20px;color:#1A1A1A;background:#FDFBF7">
  <div style="font:600 11px/1.4 sans-serif;letter-spacing:.15em;text-transform:uppercase;color:#6B6B6B">Signal State ID · Pattern Review</div>
  <h1 style="font:500 34px/1.1 Georgia,serif;margin:8px 0">${data.title}</h1>
  <div style="font:400 12px/1.7 sans-serif;color:#6B6B6B;margin:8px 0 4px">
    Generated ${new Date().toLocaleDateString()} · ${data.count ?? data.total_uses ?? 0} completed check-ins
    ${data.total_uses ? ` · ${data.total_uses} total interactions` : ""}
    ${data.evidence ? ` · Evidence: ${data.evidence}` : ""}
  </div>
  <p style="font:400 14px/1.6 sans-serif;color:#474747">${data.opening}</p>
  <p style="font:400 12px/1.5 sans-serif;color:#6B6B6B">${data.confidence_note}</p>
  <hr style="border:none;border-top:1px solid #EBE9E4;margin:16px 0">${rows}
  <div style="background:#F5F3ED;padding:14px;border-radius:10px;margin-top:16px;font:400 12px/1.6 sans-serif;color:#474747">${data.limitations}</div>
  <p style="font:400 11px/1.6 sans-serif;color:#9a9a94;margin-top:12px">This is not a psychological or medical assessment. It makes no causal claims. It is built only from your own selections and feedback, and you can export or permanently delete all of your data at any time in Settings.</p>
  </body></html>`);
  w.document.close();
  setTimeout(() => { w.focus(); w.print(); }, 400);
}

export default function Pattern() {
  const { deviceId, user } = useApp();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [showLimits, setShowLimits] = useState(false);

  useEffect(() => {
    track("pattern_viewed");
    http.get(`/pattern/${deviceId}`).then(({ data }) => setData(data)).catch(() => setData({ unlocked: false, count: 0, needed: 14 }));
  }, [deviceId]);

  if (!data) return <Shell><div className="p-8 text-[#6B6B6B]">Loading…</div></Shell>;

  if (!data.unlocked) {
    const remaining = data.needed - data.count;
    const pct = (data.count / data.needed) * 100;
    return (
      <Shell>
        <div className="px-6 pt-14 pb-28">
          <h1 className="font-serif-display text-4xl">Pattern</h1>
          <div className="mt-10 ss-card p-6">
            <p className="font-mono-ss text-sm text-[#6B6B6B]">
              {data.count} of {data.needed} completed check-ins toward your first Pattern Review
            </p>
            <div className="mt-4 h-[3px] w-full bg-[#EBE9E4] rounded-full overflow-hidden">
              <div className="h-full bg-[#C9A227] transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-6 text-[15px] leading-relaxed text-[#474747]">
              {remaining === 1
                ? "One more completed check-in will make your first Pattern Review possible."
                : `${remaining} more completed check-ins will make your first Pattern Review possible.`}
            </p>
            <p className="mt-3 text-[13px] text-[#6B6B6B]">Your progress does not expire. Missed days do not reset it.</p>
          </div>
        </div>
      </Shell>
    );
  }

  if (data.premium_locked) {
    return (
      <Shell>
        <div className="px-6 pt-14 pb-28">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6B6B6B]">Pattern Review</span>
          <h1 className="mt-2 font-serif-display text-4xl leading-tight">Your first pattern is ready.</h1>
          <div className="mt-8 ss-card p-6" data-testid="pattern-locked">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#F7F2D7] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8a6d10]">
              <Sparkles size={13} strokeWidth={2} /> Plus
            </div>
            <p className="mt-4 text-[15px] leading-relaxed text-[#474747]">
              You've logged {data.count} completed check-ins — enough to reveal your first Signal Pattern: which
              states recur, what tends to trigger them, and what has actually helped. Unlock the full
              reading with Signal State ID Plus.
            </p>
            <div className="mt-5 space-y-2.5 opacity-90">
              {["Most frequent response mode", "What appears to help you", "Your next seven-use experiment"].map((t, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-[#EBE9E4] bg-[#FBFAF6] p-3">
                  <Lock size={15} strokeWidth={1.6} className="text-[#6B6B6B] flex-shrink-0" />
                  <span className="text-[13.5px] text-[#474747] blur-[1.5px] select-none">{t}</span>
                </div>
              ))}
            </div>
            <button data-testid="pattern-unlock-plus" onClick={() => nav("/plus")}
              className="mt-6 w-full bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-4 font-medium ss-tap ss-focus hover:-translate-y-0.5">
              {user ? "Unlock with Plus" : "Sign in & unlock with Plus"}
            </button>
            <p className="mt-3 text-center text-[12.5px] text-[#6B6B6B]">
              Your check-ins stay free and unlimited.
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="px-6 pt-14 pb-28">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6B6B6B]">Pattern Review</span>
        <h1 className="mt-2 font-serif-display text-4xl leading-tight">{data.title}</h1>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {data.evidence && (
            <span className="inline-block rounded-full bg-[#EEF2EE] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[#2f6b45]" data-testid="pattern-evidence">
              Evidence: {data.evidence}
            </span>
          )}
          {data.confidence_level === "lower" && (
            <span className="inline-block rounded-full bg-[#F7F2D7] px-3 py-1 text-[11px] font-semibold text-[#8a6d10]">
              Lower confidence
            </span>
          )}
        </div>
        <p className="mt-5 text-[15px] leading-relaxed text-[#474747]">{data.opening}</p>
        <p className="mt-3 text-[13px] text-[#6B6B6B]">{data.confidence_note}</p>

        <div className="mt-8 space-y-3">
          {data.sections.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.03 * i }}
              className="ss-card p-5"
              data-testid={`pattern-section-${i}`}
            >
              <div className="flex items-baseline gap-3">
                <span className="font-mono-ss text-xs text-[#C9A227]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="text-[15px] font-semibold">{s.title}</h3>
              </div>
              <p className="mt-2 text-[14.5px] leading-relaxed text-[#474747]">{s.body}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 ss-card p-5 bg-[#F5F3ED]">
          <p className="text-[13px] leading-relaxed text-[#474747]">{data.limitations}</p>
        </div>

        <div className="mt-6 space-y-3">
          <button
            data-testid="pattern-pdf"
            onClick={() => printReview(data)}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-3.5 font-medium ss-tap ss-focus hover:-translate-y-0.5"
          >
            <Download size={17} strokeWidth={1.5} /> Download my Pattern Review as PDF
          </button>
          <button
            data-testid="pattern-continue"
            onClick={() => nav("/navigate")}
            className="w-full inline-flex items-center justify-center gap-2 border border-[#1A1A1A] rounded-full px-6 py-3.5 font-medium ss-tap ss-focus"
          >
            <Compass size={17} strokeWidth={1.5} /> Continue building my pattern
          </button>
          <button
            data-testid="pattern-limits-toggle"
            onClick={() => setShowLimits((v) => !v)}
            className="w-full inline-flex items-center justify-center gap-2 text-[13.5px] text-[#474747] hover:text-[#1A1A1A] ss-focus transition-colors py-2"
          >
            <Info size={15} strokeWidth={1.5} /> What this review can and cannot tell me
          </button>
          {showLimits && (
            <motion.div
              initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
              className="overflow-hidden ss-card p-5"
              data-testid="pattern-limits-detail"
            >
              <p className="text-[13.5px] leading-relaxed text-[#474747]">
                This review reflects only the choices and feedback you entered. It can suggest which
                conditions and responses recur for you, and which actions you rated as helpful. It
                cannot diagnose a condition, prove that any action caused a change, or replace an
                assessment from an appropriate professional. You can export or permanently delete all
                of this data at any time from Settings.
              </p>
            </motion.div>
          )}
        </div>
      </div>
    </Shell>
  );
}
